<template>
    <div class="resource-status-card" :id="`resource-card-${resource.resourceId}`"
        :style="`background-color:${resource.status.color}`" @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave">

        <div v-if="showTooltip" class="custom-cursor-tooltip" :style="`border-top-color: ${resource.status.color}`">
            <div v-if="loadingOrders" class="text-center p-2">
                <b-spinner small variant="primary"></b-spinner>
            </div>
            <div v-else-if="orders.length === 0" class="p-2 text-center text-muted">
                {{ $t('Nenhuma ordem encontrada') || 'NoOrdersFound' }}
            </div>
            <div v-else class="orders-tooltip-list">
                <div class="tooltip-header font-weight-bold pb-1 border-bottom"
                    :style="`border-bottom-color: ${resource.status.color} !important`">
                    {{ $t('Próximas Ordens') || 'NextOrders' }}
                </div>
                <div v-for="(item, index) in orders" :key="index" class="order-tooltip-item"
                    :class="{ 'bg-light-gray': index % 2 === 0, 'bg-white': index % 2 !== 0 }">
                    <div class="d-flex justify-content-between align-items-center">
                        <span class="font-weight-bold order-number">{{ item.order.number }}</span>
                        <span class="order-time">{{ formatDateTime(item.operation.startTime) }}</span>
                    </div>
                    <div class="text-truncate order-product" :title="item.operation.productName">
                        {{ item.operation.productName }}
                    </div>
                    <hr v-if="index < orders.length - 1"
                        :style="`border-color: ${resource.status.color} !important; opacity: 0.2`" />
                </div>
            </div>
        </div>

        <div class="card__header">
            <div class="code">{{ resource.resourceName }}</div>
            <div class="info-row" :style="`color:${resource.status.color}`">
                <div class="status" :style="`border-color:${resource.status.color}`">
                    {{ $t(resource.status.name) }}
                </div>
                <div class="duration" :style="`border-color:${resource.status.color}`">
                    <span class="icon">
                        <TimeIcon :style="`fill:${resource.status.color}`" />
                    </span>
                    <ResourceDuration :resource="resource" />
                </div>
            </div>
        </div>
        <div v-if="resource.orderId === 0" class="card__not_found">
            {{ $t("OrderNotDefined") }}
        </div>
        <div v-else class="card__body" style="border-bottom-left-radius: 16px; border-bottom-right-radius: 16px;">
            <div v-if="favoritePage" class="first__row">
                <div class="group__block">
                    <div class="label">{{ $t("Group") }}</div>
                    <div class="value">{{ resource.groupName }}</div>
                </div>
                <div class="center__block">
                    <div class="label">{{ $t("Center") }}</div>
                    <div class="value">{{ resource.workcenterName }}</div>
                </div>
            </div>
            <div class="second__row">
                <div class="prod__block">
                    <div class="label">Prod</div>
                    <div class="value">{{ resource.prod.toFixed(2) }}%</div>
                </div>
                <div class="perf__block">
                    <div class="label">Perf</div>
                    <div class="value">{{ resource.perf.toFixed(2) }}%</div>
                </div>
            </div>
            <div class="third__row">
                <div class="order__block">
                    <div class="label">{{ $t("ORDER") }}</div>
                    <div class="value">{{ resource.orderName }}</div>
                </div>
                <div class="quantity__block">
                    <div class="label">{{ $t("QTY") }}</div>
                    <div class="value">{{ resource.orderQuantity }}</div>
                </div>
                <div v-if="favoritePage" class="qty-prod__block">
                    <div class="label">{{ $t("QTYPROD") }}</div>
                    <div class="value">{{ resource.produced }}</div>
                </div>
            </div>
            <div class="fourth__row">
                <div v-if="!favoritePage" class="qty-prod__block">
                    <div class="label">{{ $t("QTYPROD") }}</div>
                    <div class="value">{{ resource.produced }}</div>
                </div>
                <div v-if="favoritePage" class="rework__block">
                    <div class="label">{{ $t("REWORK") }}</div>
                    <div class="value">{{ resource.reworkQuantity }}</div>
                </div>
                <div v-else class="refuse__block">
                    <div class="label">{{ $t("REFUSE") }}</div>
                    <div class="value">{{ resource.scrap }}</div>
                </div>
                <div class="qty-good__block">
                    <div class="label">{{ $t("QTYGOOD") }}</div>
                    <div class="value">{{ Math.max(0, resource.good || 0) }}</div>
                </div>
            </div>
            <div class="fifth__row" style="padding-bottom: 0 !important; margin-bottom: 0 !important;">
                <div class="material__block">
                    <div class="label">Material:</div>
                    <div class="value">{{ resource.materialName }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import ResourceDuration from "@/views/pages/operator/components/Resources/ResourceDuration.vue";

export default {
    props: {
        resource: {
            type: Object,
            default: () => ({
                resourceId: null,
                resourceName: "",
                orderId: null,
                orderName: "",
                appointmentType: null,
                orderQuantity: null,
                materialId: null,
                materialName: "",
                produced: null,
                scrap: null,
                good: null,
                prod: null,
                perf: null,
                isFavorite: false,
                durationInHours: 0,
                status: {
                    statusId: null,
                    name: "",
                    color: ""
                }
            })
        },
        favoritePage: {
            type: Boolean,
            default: false
        },
        siteId: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            orders: [],
            loadingOrders: false,
            showTooltip: false
        };
    },
    computed: {
        appointmentTypeLabel() {
            return this.resource.appointmentType === 1
                ? this.$t("ManualAppointment")
                : this.$t("AutomaticAppointment");
        }
    },
    methods: {
        handleMouseEnter() {
            this.showTooltip = true;
            this.fetchOrders();
        },
        handleMouseLeave() {
            this.showTooltip = false;
        },
        async fetchOrders() {
            if (this.orders.length > 0) return;

            this.loadingOrders = true;
            try {
                const response = await this.$http2.get(
                    `/api/mysfc/operator/order?ResourceId=${this.resource.resourceId}&SiteId=${this.siteId}`
                );
                if (response.data && response.data.data) {
                    this.orders = response.data.data.sort((a, b) => {
                        return new Date(a.operation.startTime) - new Date(b.operation.startTime);
                    });
                }
            } catch (error) {
                console.error("Error fetching orders:", error);
            } finally {
                this.loadingOrders = false;
            }
        },
        formatDateTime(dateString) {
            if (!dateString) return "";
            const date = new Date(dateString);
            return date.toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
        }
    },
    components: {
        ResourceDuration,
        BSpinner,
        TimeIcon: () => import("@core/assets/icons/time-icon.svg"),
        MinusIcon: () => import("@core/assets/icons/minus-icon.svg"),
        FavIcon: () => import("@core/assets/icons/favorite-icon.svg")
    }
};
</script>

<style lang="scss">
.custom-cursor-tooltip {
    position: absolute;
    bottom: 48px;
    left: 0;
    width: 100%;
    z-index: 10;
    background-color: #ffffff;
    border-radius: 0;
    padding: 16px;
    box-shadow: 0 -4px 24px 0 rgba(0, 0, 0, 0.1);
    pointer-events: auto;
    max-height: 300px;
    overflow-y: auto;
    border-top: 4px solid; // Color set via inline style

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f8f8f8;
    }

    &::-webkit-scrollbar-thumb {
        background: #c3c3c3;
        border-radius: 6px;
    }

    .tooltip-header {
        color: #5e5873;
        font-size: 1.1rem;
        border-bottom: 1px solid; // Color set via inline style
        margin-bottom: 0.1rem;
    }

    .order-tooltip-item {
        font-size: 1rem;
        margin-bottom: 0.1rem; // Reduced margin
        border-radius: 4px;

        &.bg-light-gray {
            background-color: #f8f8f8;
        }

        &.bg-white {
            background-color: #ffffff;
        }

        .order-number {
            color: #5e5873;
            font-size: 1.1rem;
        }

        .order-time {
            color: #b9b9c3;
            font-size: 0.95rem;
        }

        .order-product {
            color: #6e6b7b;
        }

        hr {
            margin: 0.1rem 0; // Reduced margin
            border-top: 1px solid; // Color set via inline style
        }
    }
}

.resource-orders-tooltip {
    display: none; // Legacy cleanup
}

.resource-status-card {
    position: relative;
    min-height: 374px;
    height: auto;
    border-radius: 16px;
    overflow: hidden;
    color: #fff;
    font-family: sans-serif;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: transform 0.2s ease;
    width: 100%;
    min-width: unset;
    max-width: 100%;

    .card__header {
        text-align: center;

        .code {
            padding: 8px;
            color: #fff;
            text-align: center;
            font-size: 26px;
            font-weight: 600;
            line-height: 36px;
        }

        .info-row {
            display: flex;
            font-size: 22px;
            font-weight: 600;
            line-height: 30px;
            background: #fff;

            .status,
            .duration {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                width: 100%;
                text-align: center;
                border: 1px solid;
            }

            .duration {
                max-width: 132px;
            }

            .status {
                padding: 16px 0;
                border-right: none;
            }

            .icon {
                svg {
                    display: flex;
                    width: 16px;
                    height: 16px;
                }
            }
        }
    }

    .card__not_found {
        flex-grow: 1;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        padding: 16px;
        text-align: center;
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        line-height: 26px;
    }

    .card__body {
        height: 100%;

        .first__row,
        .second__row,
        .fourth__row {
            display: flex;
            gap: 16px;
            padding: 16px;
            border-bottom: 1px solid #ffffff;

            .group__block,
            .center__block,
            .prod__block,
            .perf__block,
            .qty-prod__block,
            .refuse__block,
            .rework__block,
            .qty-good__block {
                width: 100%;

                .label {
                    color: #fff;
                    text-align: center;
                    font-size: 16px;
                    font-weight: 400;
                    line-height: 22px;
                }

                .value {
                    color: #fff;
                    text-align: center;
                    font-size: 16px;
                    font-weight: 600;
                    line-height: 26px;

                    max-width: 165px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }

            .prod__block,
            .perf__block {
                display: flex;
                gap: 4px;
                align-items: center;
                justify-content: center;
            }
        }

        .third__row,
        .fifth__row {
            display: flex;
            gap: 16px;
            padding: 16px;
            padding-bottom: 0 !important;

            .order__block,
            .quantity__block,
            .material__block,
            .qty-prod__block {
                width: 100%;
                text-align: center;

                .label {
                    color: #fff;
                    text-align: center;
                    font-size: 16px;
                    font-weight: 400;
                    line-height: 22px;
                }

                .value {
                    color: #fff;
                    text-align: center;
                    font-size: 16px;
                    font-weight: 600;
                    line-height: 26px;
                }
            }

            .material__block {
                display: flex;
                gap: 4px;
                align-items: center;
                justify-content: center;
            }
        }
    }

    .card__footer {
        background-color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 13px;
        border: 1px solid;
        border-radius: 0 0 16px 16px;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        letter-spacing: 0.175px;

        .remove-icon {
            font-size: 16px;

            svg {
                display: flex;
                width: 16px;
                height: 16px;
            }
        }
    }
}

@media (max-width: 480px) {
    .resource-status-card {
        .card__header .code {
            font-size: 16px;
            line-height: 20px;
        }
    }
}

@media (min-width: 481px) {
    .resource-status-card {
        min-height: 280px;

        .card__header {
            .code {
                padding: 4px;
                font-size: 18px;
                line-height: 22px;
            }

            .info-row {
                font-size: 14px;
                line-height: 20px;

                .status {
                    padding: 4px 0;
                }
            }
        }

        .card__body {

            .first__row,
            .second__row,
            .third__row,
            .fourth__row,
            .fifth__row {
                padding: 6px 10px;
                gap: 4px;

                .label {
                    font-size: 11px;
                    line-height: 14px;
                }

                .value {
                    font-size: 13px;
                    line-height: 16px;
                }
            }
        }
    }
}

@media (min-width: 1400px) {
    .resource-status-card {
        min-height: 260px;

        .card__header .code {
            padding: 4px;
            font-size: 20px;
            line-height: 24px;
        }

        .card__header .info-row {
            font-size: 16px;
            line-height: 22px;

            .status {
                padding: 8px 0;
            }
        }

        .card__body {

            .first__row,
            .second__row,
            .third__row,
            .fourth__row,
            .fifth__row {
                padding: 8px;
                gap: 6px;

                .label {
                    font-size: 12px;
                    line-height: 16px;
                }

                .value {
                    font-size: 13px;
                    line-height: 18px;
                }
            }
        }
    }
}
</style>
